import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import {
  BookOpen,
  Calendar,
  Eye,
  Flag,
  FolderOpen,
  LayoutDashboard,
  MessageCircle,
  ReceiptText,
  StickyNote,
  Target,
} from "lucide-react";

import { EngagementStatusDropdown } from "~/components/engagement/engagement-status-dropdown";
import type { WorkspaceNavGroup } from "~/components/engagement/workspace-shell";
import { EngagementWorkspaceShell } from "~/components/engagement/workspace-shell";
import { ButtonRouter } from "~/components/ui/button";
import { EngagementStatus } from "~/lib/engagement-status";
import { useUserInfoForEngagement } from "~/lib/queries/engagementQuery";
import { engagementConversationQueryOptions } from "~/lib/queries/messagingQuery";
import { useAllEngagementSessions } from "~/lib/queries/sessionQuery";
import { useClientSharedStatus } from "~/lib/queries/userSharedQuery";
import { clientProgramPhase } from "~/lib/shared/client-programs";

export const Route = createFileRoute(
  "/_user/my-coaching/clients/programs/$engagementId",
)({
  staticData: { title: "Client" },
  component: EngagementDetailLayout,
});

function EngagementDetailLayout() {
  const { engagementId } = Route.useParams();

  const { data: userInfo, isLoading: userInfoLoading } =
    useUserInfoForEngagement(engagementId);
  const { data: sharedStatus } = useClientSharedStatus(userInfo?.userId);
  const { data: sessionData, isLoading: sessionLoading } =
    useAllEngagementSessions(engagementId);
  const { data: conversation } = useQuery(
    engagementConversationQueryOptions(engagementId),
  );

  const status = (userInfo?.status ||
    EngagementStatus.ACTIVE) as EngagementStatus;

  const navGroups: WorkspaceNavGroup[] = [
    {
      label: "Coaching",
      items: [
        {
          id: "overview",
          label: "Overview",
          to: "/my-coaching/clients/programs/$engagementId",
          icon: LayoutDashboard,
          exact: true,
        },
        {
          id: "sessions",
          label: "Sessions",
          to: "/my-coaching/clients/programs/$engagementId/sessions",
          icon: Calendar,
        },
        {
          id: "messages",
          label: "Messages",
          to: "/my-coaching/clients/programs/$engagementId/messages",
          icon: MessageCircle,
          badge: conversation?.unreadCount,
        },
        {
          id: "homework",
          label: "Homework",
          to: "/my-coaching/clients/programs/$engagementId/homework",
          icon: BookOpen,
        },
        {
          id: "resources",
          label: "Resources",
          to: "/my-coaching/clients/programs/$engagementId/coaching-resources",
          icon: FolderOpen,
        },
      ],
    },
    {
      label: "Reference",
      items: [
        {
          id: "goals",
          label: "Goals",
          to: "/my-coaching/clients/programs/$engagementId/coaching-goals-and-outcomes",
          icon: Target,
        },
        {
          id: "notes",
          label: "Notes",
          to: "/my-coaching/clients/programs/$engagementId/notes",
          icon: StickyNote,
        },
        {
          id: "admin",
          label: "Billing & documents",
          to: "/my-coaching/clients/programs/$engagementId/admin",
          icon: ReceiptText,
        },
        ...(sharedStatus?.isSharedOverallViewWithCoach
          ? [
              {
                id: "client-vision",
                label: "Client's vision",
                to: "/my-coaching/clients/programs/$engagementId/client-vision",
                icon: Eye,
              },
            ]
          : []),
        ...(sharedStatus?.isSharedGoalsWithCoach
          ? [
              {
                id: "client-goals",
                label: "Client's goals",
                to: "/my-coaching/clients/programs/$engagementId/client-goals",
                icon: Flag,
              },
            ]
          : []),
      ],
    },
  ];

  return (
    <EngagementWorkspaceShell
      engagementId={engagementId}
      backTo="/my-coaching/clients/programs"
      backLabel="Back to client programs"
      // Until the status is known the list picks its own default tab.
      backSearch={
        userInfo?.status
          ? { tab: clientProgramPhase(userInfo.status as EngagementStatus) }
          : undefined
      }
      kicker="Client program"
      name={userInfo?.name || "Client"}
      avatarUrl={userInfo?.profilePictureUrl}
      headerLoading={userInfoLoading}
      statusSlot={
        <EngagementStatusDropdown
          currentStatus={status}
          engagementId={engagementId}
        />
      }
      actions={
        <ButtonRouter
          to="/my-coaching/clients/programs/$engagementId/messages"
          params={{ engagementId }}
          className="gap-2"
        >
          <MessageCircle className="size-4" />
          Message client
        </ButtonRouter>
      }
      progress={
        sessionData?.progress
          ? {
              completed: sessionData.progress.sessionsCompleted,
              total: sessionData.progress.sessionsTotal,
            }
          : undefined
      }
      progressLoading={sessionLoading}
      navGroups={navGroups}
    >
      <Outlet />
    </EngagementWorkspaceShell>
  );
}
